import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CART_STORAGE_KEY } from '@scan2eat/shared';

export interface CartLine {
  menuItemId: string;
  nameSnapshot: string;
  unitPriceMinor: number;
  quantity: number;
  note?: string;
  /** Devise du plat, reprise de l'affichage. Absente dans les paniers anciens. */
  currency?: string;
}

export interface CartState {
  lines: CartLine[];
  customerNote: string;

  addLine: (line: Omit<CartLine, 'quantity'>, quantity?: number) => void;
  setQuantity: (menuItemId: string, quantity: number) => void;
  setLineNote: (menuItemId: string, note: string) => void;
  removeLine: (menuItemId: string) => void;
  restoreLine: (line: CartLine, index: number) => void;
  setCustomerNote: (note: string) => void;
  clear: () => void;

  itemCount: () => number;
  totalMinor: () => number;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      lines: [],
      customerNote: '',

      addLine: (line, quantity = 1) =>
        set((state) => {
          const existing = state.lines.find((l) => l.menuItemId === line.menuItemId);
          if (existing) {
            return {
              lines: state.lines.map((l) =>
                l.menuItemId === line.menuItemId ? { ...l, quantity: l.quantity + quantity } : l,
              ),
            };
          }
          return { lines: [...state.lines, { ...line, quantity }] };
        }),

      setQuantity: (menuItemId, quantity) =>
        set((state) => ({
          lines: quantity <= 0
            ? state.lines.filter((l) => l.menuItemId !== menuItemId)
            : state.lines.map((l) => (l.menuItemId === menuItemId ? { ...l, quantity } : l)),
        })),

      setLineNote: (menuItemId, note) =>
        set((state) => ({
          lines: state.lines.map((l) =>
            l.menuItemId === menuItemId ? { ...l, note: note || undefined } : l,
          ),
        })),

      removeLine: (menuItemId) =>
        set((state) => ({ lines: state.lines.filter((l) => l.menuItemId !== menuItemId) })),

      restoreLine: (line, index) =>
        set((state) => {
          if (state.lines.some((l) => l.menuItemId === line.menuItemId)) return state;
          const lines = [...state.lines];
          lines.splice(Math.min(index, lines.length), 0, line);
          return { lines };
        }),

      setCustomerNote: (customerNote) => set({ customerNote }),
      clear: () => set({ lines: [], customerNote: '' }),

      itemCount: () => get().lines.reduce((sum, l) => sum + l.quantity, 0),
      totalMinor: () => get().lines.reduce((sum, l) => sum + l.unitPriceMinor * l.quantity, 0),
    }),
    { name: CART_STORAGE_KEY, partialize: (s) => ({ lines: s.lines, customerNote: s.customerNote }) },
  ),
);
